import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'

interface FAQItem {
  id: number
  question: string
  answer: string
  isExpanded: boolean
}

const FAQ: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQItem[]>([
    {
      id: 1,
      question: "为什么要迁移到外区App Store？",
      answer: "由于中国区App Store的政策调整，漫时光1.1版本已在中国区下架。为了获得最新功能、更好的用户体验和持续的技术支持，建议用户迁移到外区App Store。",
      isExpanded: false
    },
    {
      id: 2,
      question: "迁移后我的数据会丢失吗？",
      answer: "不会。由于全程不卸载原App，所有数据都会自动保留。包括漫画收藏、阅读进度、设置等所有信息都会完整保留，无需手动备份和恢复。",
      isExpanded: false
    },
    {
      id: 3,
      question: "如何获取外区Apple账号？",
      answer: "我们直接在迁移步骤中为您提供了外区Apple账号。点击'切换App Store区域'步骤的详细说明，即可看到可用的外区Apple账号，点击复制按钮即可复制账号信息。",
      isExpanded: false
    },
    {
      id: 4,
      question: "在外区App Store如何找到漫时光？",
      answer: "在外区App Store中直接搜索'漫时光'即可找到外区稳定版本。无需搜索英文名称，直接使用中文搜索即可。",
      isExpanded: false
    },
    {
      id: 5,
      question: "提供的Apple ID账号安全吗？如何正确使用？",
      answer: "⚠️ 重要安全提醒：提供的Apple ID仅限在App Store应用中登录使用，严禁在手机「设置」中登录！在设置中登录可能导致手机变砖，后果自负。正确使用方法：1) 只在App Store中登录；2) 下载完成后立即退出；3) 重新登录您的个人账号。",
      isExpanded: false
    },
    {
      id: 6,
      question: "新版本有什么新功能？",
      answer: "漫时光1.1版本新增了离线阅读、智能推荐、阅读统计、主题切换等功能，同时优化了性能和用户体验，修复了已知问题。",
      isExpanded: false
    }
  ])

  const toggleFAQ = (faqId: number) => {
    setFaqs(faqs.map(faq =>
      faq.id === faqId
        ? { ...faq, isExpanded: !faq.isExpanded }
        : faq
    ))
  }

  return (
    <section id="faq-section" className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <HelpCircle className="w-8 h-8 text-primary-600" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              常见问题
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            关于版本迁移的常见问题解答。如果您的问题没有在这里找到答案，
            请随时联系我们的客服团队。
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden"
            >
              <motion.button
                className="w-full flex items-center justify-between p-6 text-left"
                onClick={() => toggleFAQ(faq.id)}
                whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}
                whileTap={{ scale: 0.98 }}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: faq.isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  {faq.isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {faq.isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}

export default FAQ
